<template>
	<div class="px-3 mt-4">
		<p class="flight-pack-text text-grey-900">{{ content.flight_select_pack }}</p>
		<div class="d-flex align-items-center gap-2">
			<div v-for="packObj in effectivePackagesToDisplay" :key="packObj.codePackage" @click="handlePackageChange(packObj as { codePackage: string, packageId: string })"
				class="package-tab d-flex align-items-center justify-content-between p-3   col" :class="packCode === packObj.codePackage ? 'active' : 'text-grey-900'
					">
				<span class="d-flex align-items-center">
					<template v-for="(component, componentIndex) in getPackageCompositionConfig(
						packObj.codePackage
					)" :key="`${packObj.codePackage}-${componentIndex}`">
						<span v-if="componentIndex > 0" class="mx-2 text-grey-900">+</span>
						<span class="d-flex align-items-center me-1 flex-column">
							<svg width="20" height="20" class="icon me-1">
								<use :xlink:href="`#___icon___${component.icon}`"></use>
							</svg>
							<span class="package-label">{{ getContentKey(component) }}</span>
						</span>
					</template>
				</span>

				<!-- Discount Badge -->
				<div class="d-flex justify-content-start align-items-center">
					<div v-if="
						packObj.codePackage !== 'HS' &&
						promos[packObj.codePackage] &&
						promos[packObj.codePackage].perCent >= 5
					" class="d-flex align-items-center justify-content-center prestation-discount-container f bg-primary-orange ms-1 ms-xl-3">
						<div class="prestation-discount subcaption-strong text-primary-white px-2 py-1 text-nowrap">
							{{ promoString }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue"
import { useStore } from "vuex"

// Computed
import packagesToDisplay from "@/vue/planning/computed/packagesToDisplay"

// Config
import packageCompositionConfig from "@/config/packageCompositionConfig"
import { type PackageComponent } from "@/config/packageCompositionConfig"

// Logic
import { isCS } from "@/vue/planning/logic/cs"
import getPackageComposition from "@/vue/planning/logic/getPackageComposition"

// Utils 
import { hasChangedTransportType } from "@/utils/transportation"
import { airportCodeMap } from "@/config/airportCodeMap"
import flightPackCode from "@/config/flightPackCode"
import transportPackCode from "@/config/transportPackCode"
import getSiteFromDomain from "@/vue/planning/logic/getSiteFromDomain";
import displayPrice from "@/displayPrice/displayPrice"
import trainPackCode from "@/config/trainPackCode"
import { transportPacks } from "@/config/transportPackagePriorities"

type PackPromo = { perCent: number; perValue: number }


const store = useStore()

// Reactive data
const localPackagesToDisplay = ref<{ codePackage: string, packageId?: string }[]>([])

// Computed
const packCode = computed<string>(() => store.state.packCode)
const isLocaleNL = computed<boolean>(() => store.getters.isLocaleNL)
const isLocaleEN = computed<boolean>(() => store.getters.isLocaleEN)
const content = computed<Record<string, string>>(() => store.state.content)
const availablePackCodes = computed<{ codePackage: string }[]>(() => store.getters.availablePackCodes)
// const maxPromosFromAvailablePackages = computed(() => store.getters.maxPromosFromAvailablePackages)

const promoString = computed(() => {
	if (getSiteFromDomain(window.location.hostname) === "www.uk.travelski.com") {
		return `-${displayPrice(promos.value[packCode.value as keyof typeof promos.value].perValue*100, store.getters.locale)}`;
	}
	return `-${promos.value[packCode.value as keyof typeof promos.value].perCent}%`;
});

const effectivePackagesToDisplay = computed(() => {
	
	let result: { codePackage: string, packageId?: string }[] = [];

	if (localPackagesToDisplay.value && localPackagesToDisplay.value.length > 0) {
		result = localPackagesToDisplay.value;
	} else if (packagesToDisplay.value && packagesToDisplay.value.length > 0) {
		result = packagesToDisplay.value;
	} else if (availablePackCodes.value && availablePackCodes.value.length > 0) {
		result = availablePackCodes.value;
	}

	return result;
})

// Methods
const getPackageCompositionConfig = (packageCode: string) => {
	const composition = packageCompositionConfig[packageCode]?.components || []
	return composition
}

const getContentKey = (component: PackageComponent) => {
	// Use short version for Dutch and English locales
	let keyToUse = component.contentKey
	if ((isLocaleNL.value || isLocaleEN.value) && component.contentKeyShort) {
		keyToUse = component.contentKeyShort
	}
	return content.value[keyToUse]
}

const handlePackageChange = async (packObj: {
	codePackage: string
	packageId: string
}) => {
	const isTransportPack = transportPacks.includes(packObj.codePackage)
	if (packCode.value === packObj.codePackage) {
		store.dispatch("setPackagesMobileDropdownOpen", false)
		return
	}

	// First update package composition if needed
	if (packObj.codePackage !== "HS" && !isCS(packObj)) {
		await getPackageComposition(store)
	}

	const changedTransportType = hasChangedTransportType(store.state.packCode, packObj.codePackage, isTransportPack)

	// Then update the package
	await store.dispatch("setPackage", packObj.codePackage)
	store.state.packageId = packObj.packageId

	// Check if the new package is a flight pack or train pack and select the appropriate origin
	const isFlightPackage = flightPackCode.includes(packObj.codePackage)
	const isTrainPackage = trainPackCode.includes(packObj.codePackage)
	if (isFlightPackage) {
		store.commit("setTransportationSelected", true)
		// Always select the first available origin when switching to a flight package tab
		store.dispatch("selectDefaultFlightOrigin", { overwrite: true, forceFlightPackCode: true })
	} else if (isTrainPackage) {
		store.commit("setTransportationSelected", true)
		// Always select the default train origin when switching to a train package tab
		store.dispatch("selectDefaultTrainOrigin", { overwrite: false, forceTrainPackCode: false })
	} else if (isTransportPack) {
		store.commit("setTransportationSelected", true)
		const overwrite = store.state.flightOrigin === airportCodeMap.get("None")?.name || changedTransportType
		store.dispatch("selectDefaultFlightOrigin", overwrite)
	} else {
		store.commit("setTransportationSelected", false)
		store.commit("setFlightOrigin", airportCodeMap.get("None")?.name)
	}

}


const promos = computed((): Record<string, PackPromo> => {
	const rawData = store.getters.getWiypCallByFamilyComposition(store.state.participants.join('-')) || {};
	const promoObject: Record<string, PackPromo> = {};
	if (rawData && typeof rawData === 'object') {
		availablePackCodes.value.forEach((packObj: { codePackage: string, packageId?: string }) => {
			// We NEED to load prestIdFusion/prestIdSource from prestmaps but we do not have them
		
			let maxPromo: PackPromo = { perCent: 0, perValue: 0 };
			// Loop through all values (usually just one key, but future-proof)
			// { "LON": { "PFF": {"1000000032": {wiypPrices: [{publicPriceWiyp: 100, sellingPriceWiyp: 90}]}}}}
			Object.values(rawData).forEach((valueOriginObject: any) => {
				//{ "PFF": {"1000000032": {wiypPrices: [{publicPriceWiyp: 100, sellingPriceWiyp: 90}]}}}
				Object.entries(valueOriginObject).forEach(([packageCode, valuePackageObject]: [string, any]) => {
					//{ "1000000032": {wiypPrices: [{publicPriceWiyp: 100, sellingPriceWiyp: 90}]}}}
					if ( packageCode === packObj.codePackage && valuePackageObject && typeof valuePackageObject === 'object' ) {
						Object.values(valuePackageObject).forEach((valuePackageIdObject: any) => {
							//{wiypPrices: [{publicPriceWiyp: 100, sellingPriceWiyp: 90}]}}}
							if (valuePackageIdObject && Array.isArray((valuePackageIdObject as any).wiypPrices)) {
								(valuePackageIdObject as any).wiypPrices.forEach((promoPrice: any) => {
									// need to check that prestId is in variant
									if (
										typeof promoPrice.publicPriceWiyp === 'number' &&
										typeof promoPrice.sellingPriceWiyp === 'number' &&
										promoPrice.publicPriceWiyp !== 0
									) {
										const promo = Math.round(
											((promoPrice.publicPriceWiyp - promoPrice.sellingPriceWiyp) /
												promoPrice.publicPriceWiyp) *
												100
										);
										if (promo > maxPromo.perCent) {
											maxPromo = { perCent: promo, perValue: Math.floor(promoPrice.publicPriceWiyp - promoPrice.sellingPriceWiyp)};
										}
									}
								});
							}	
						});
					}
				});
			});
			promoObject[packObj.codePackage] = maxPromo;
		})
	}
	return promoObject;
})

onMounted(() => {
	if (flightPackCode.includes(packCode.value) && store.state.participants.length > 9) {
		store.dispatch("setParticipants", store.state.participants.slice(0, 9));
	}
	if ((!packagesToDisplay.value || packagesToDisplay.value.length === 0) &&
		availablePackCodes.value && availablePackCodes.value.length > 0) {
		localPackagesToDisplay.value = [...availablePackCodes.value] as { codePackage: string, packageId: string }[]
	}
})

</script>

<style scoped>
.package-tab {
	height: 80px;
	border-radius: var(--border-radius-card);
	border: 1px solid var(--grey-300);
	cursor: pointer;
}

.package-tab.active {
	border: 1px solid var(--primary-blue);
}

.prestation-discount-container {
	border-radius: 37px;
}

.flight-pack-text {
	font-size: 14px;
}
</style>
